'use client';

/**
 * AstorBriefingCard Component
 *
 * Mint-bordered hero card used as the page-level "AI digest" surface across
 * portals (consumer Home, business control center, etc.). Renders a pulsing
 * AstorAvatar, eyebrow + timestamp meta row, headline, numbered tone-colored
 * bullet rows, and an optional collapsible "overnight activity" footer.
 *
 * Reusable for ANY page-level AI briefing — not borrower-specific.
 *
 * - Headline is capped at `subtitle1` (17px / weight 600) so it stays below
 *   the page-title h5 ceiling (per portal typography rules).
 * - Touch targets ≥44px (Glass primitives).
 * - Theme tokens + alpha() only — no hex literals.
 */

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Skeleton from '@mui/material/Skeleton';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import { type ReactNode, useState } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { iconSizes } from '../../theme/typography';
import { composeSx } from '../../utils/styledUtils';
import { GlassButton } from '../GlassButton';
import { GlassIconButton, GlassIconButtonSize } from '../GlassIconButton';

// ============================================================================
// Types
// ============================================================================

export type AstorBriefingBulletTone = 'amber' | 'mint' | 'id' | 'red';

export interface AstorBriefingBullet {
  /** Stable id for React keys. */
  id: string;
  /** Tone drives bullet bg/border + numbered-circle color. */
  tone: AstorBriefingBulletTone;
  /** Bullet body — `ReactNode` (NOT raw HTML). Pass `<strong>` spans inline. */
  text: ReactNode;
  /** Optional ghost-button action rendered at the right of the row. */
  action?: {
    label: ReactNode;
    onClick: () => void;
  };
}

export interface AstorBriefingCardProps {
  /** Primary one-line headline (e.g. "3 things need you before Friday"). */
  headline: ReactNode;
  /** Numbered tone-colored bullets. */
  bullets: AstorBriefingBullet[];
  /** Optional overnight activity strings — rendered inside a Collapse toggle. */
  overnight?: string[];
  /** Eyebrow meta (e.g. "Updated 6:30 AM PT"). */
  timestamp?: ReactNode;
  /** Refresh handler — renders an icon button when provided. */
  onRefresh?: () => void;
  /** "Open Astor" primary action handler. */
  onOpenAstor?: () => void;
  /** Eyebrow label override (default "Astor briefing"). */
  eyebrow?: ReactNode;
  /** Refresh icon button aria-label. */
  refreshAriaLabel?: string;
  /** Open Astor button label. */
  openAstorLabel?: ReactNode;
  /** Show-overnight toggle label (interpolates `{count}` ↦ overnight.length). */
  showOvernightLabel?: (count: number) => ReactNode;
  /** Hide-overnight toggle label. */
  hideOvernightLabel?: (count: number) => ReactNode;
  /** Color token for the eyebrow Astor chip ring — defaults `success`. */
  astorChipColor?: 'success' | 'primary';
  /** Sx on outer wrapper. */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Tone helpers (translate spec tones to MUI palette tokens)
// ============================================================================

function toneColor(theme: Theme, tone: AstorBriefingBulletTone): string {
  switch (tone) {
    case 'amber':
      return theme.palette.warning.main;
    case 'red':
      return theme.palette.error.main;
    case 'id':
      // "id" tone in the v3 design is the indigo/info accent for in-progress/
      // with-lender stages — map to info.main so the platform palette wins.
      return theme.palette.info.main;
    default:
      return theme.palette.primary.main;
  }
}

// ============================================================================
// AstorAvatar (mint gradient + AutoAwesome icon + pulse ring)
// ============================================================================

interface AstorAvatarProps {
  size?: number;
  pulse?: boolean;
}

function AstorAvatar({ size = 26, pulse = true }: AstorAvatarProps) {
  const theme = useTheme();
  const prefersReducedMotion = useReducedMotion();
  const mint = theme.palette.primary.main;
  const mintSoft = theme.palette.primary.light;
  const shouldPulse = pulse && !prefersReducedMotion;

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {shouldPulse && (
        <Box
          component={motion.span}
          aria-hidden
          initial={{ opacity: 0.5, scale: 1 }}
          animate={{ opacity: 0, scale: 1.7 }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            backgroundColor: alpha(mint, 0.3),
          }}
        />
      )}
      <Box
        aria-hidden
        sx={{
          position: 'relative',
          width: size,
          height: size,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${mintSoft} 0%, ${mint} 100%)`,
          color: theme.palette.primary.contrastText,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 0 0 1px ${alpha(mint, 0.4)}, 0 4px 12px ${alpha(mint, 0.25)}`,
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: Math.round(size * 0.6) }} />
      </Box>
    </Box>
  );
}

// ============================================================================
// Bullet row
// ============================================================================

interface BulletRowProps {
  bullet: AstorBriefingBullet;
  index: number;
}

function BulletRow({ bullet, index }: BulletRowProps) {
  const theme = useTheme();
  const color = toneColor(theme, bullet.tone);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        backgroundColor: alpha(color, 0.08),
        border: `1px solid ${alpha(color, 0.28)}`,
      }}
    >
      <Box
        aria-hidden
        // H11: numbered bullet circle 24 → 20 to tighten the row and
        // match the cluster of icon sizes the design pass standardized on.
        sx={{
          flexShrink: 0,
          width: 20,
          height: 20,
          borderRadius: '50%',
          backgroundColor: alpha(color, 0.18),
          color,
          fontSize: '0.75rem',
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
        }}
      >
        {index + 1}
      </Box>
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          color: 'text.primary',
          lineHeight: 1.55,
          '& strong': { fontWeight: 700 },
        }}
      >
        {bullet.text}
      </Typography>
      {bullet.action && (
        <GlassButton
          variant="ghost"
          color="primary"
          size="small"
          onClick={bullet.action.onClick}
          sx={{ flexShrink: 0, minHeight: 44 }}
        >
          {bullet.action.label}
        </GlassButton>
      )}
    </Box>
  );
}

// ============================================================================
// Skeleton
// ============================================================================

function AstorBriefingCardSkeleton() {
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Skeleton variant="circular" width={26} height={26} />
        <Skeleton variant="text" width={140} />
        <Box sx={{ flex: 1 }} />
        <Skeleton variant="rounded" width={44} height={44} />
        <Skeleton variant="rounded" width={120} height={44} />
      </Box>
      <Skeleton variant="text" width="60%" height={28} sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={56} />
        ))}
      </Box>
    </Box>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AstorBriefingCard({
  headline,
  bullets,
  overnight,
  timestamp,
  onRefresh,
  onOpenAstor,
  eyebrow = 'Astor briefing',
  refreshAriaLabel = 'Refresh briefing',
  openAstorLabel = 'Open Astor',
  showOvernightLabel = (count) => `Show overnight activity (${count})`,
  hideOvernightLabel = (count) => `Hide overnight activity (${count})`,
  astorChipColor = 'success',
  sx,
}: AstorBriefingCardProps) {
  const theme = useTheme();
  const [overnightOpen, setOvernightOpen] = useState(false);
  const accent = astorChipColor === 'primary' ? theme.palette.primary.main : theme.palette.success.main;
  const hasOvernight = Array.isArray(overnight) && overnight.length > 0;

  return (
    <Box
      sx={composeSx(
        {
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 2,
          border: `1px solid ${alpha(accent, 0.4)}`,
          backgroundColor: alpha(accent, 0.06),
          backgroundImage: `radial-gradient(ellipse at top left, ${alpha(accent, 0.16)} 0%, transparent 60%)`,
          p: 2.5,
        },
        sx,
      )}
    >
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}>
        <AstorAvatar size={26} pulse />
        <Typography
          variant="overline"
          sx={{
            color: accent,
            fontWeight: 700,
            letterSpacing: '0.08em',
            lineHeight: 1,
          }}
        >
          {eyebrow}
        </Typography>
        {timestamp && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            · {timestamp}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {onRefresh && (
          <GlassIconButton
            aria-label={refreshAriaLabel}
            onClick={onRefresh}
            size={GlassIconButtonSize.Large}
            variant="subtle"
          >
            <RefreshIcon sx={{ fontSize: iconSizes.md }} />
          </GlassIconButton>
        )}
        {onOpenAstor && (
          // H10: drop the gradient variant so the CTA resolves to a flat
          // mint button (theme primary) — keeps the card's gradient
          // background as the sole gradient surface.
          <GlassButton variant="contained" color="primary" size="medium" onClick={onOpenAstor} sx={{ minHeight: 44 }}>
            {openAstorLabel}
          </GlassButton>
        )}
      </Box>

      {/* Headline — variant subtitle1 (17px) caps at the page-title ceiling. */}
      <Typography
        variant="subtitle1"
        component="div"
        sx={{
          fontWeight: 600,
          color: 'text.primary',
          mb: 2,
          lineHeight: 1.35,
        }}
      >
        {headline}
      </Typography>

      {/* Numbered bullets */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {bullets.map((bullet, idx) => (
          <BulletRow key={bullet.id} bullet={bullet} index={idx} />
        ))}
      </Box>

      {/* Overnight expandable */}
      {hasOvernight && (
        <Box sx={{ mt: 1.5 }}>
          <GlassButton
            variant="ghost"
            color="inherit"
            size="small"
            onClick={() => setOvernightOpen((v) => !v)}
            startIcon={
              overnightOpen ? (
                <ExpandLessIcon sx={{ fontSize: iconSizes.sm }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: iconSizes.sm }} />
              )
            }
            sx={{ minHeight: 44 }}
          >
            {overnightOpen ? hideOvernightLabel(overnight.length) : showOvernightLabel(overnight.length)}
          </GlassButton>
          <Collapse in={overnightOpen} unmountOnExit>
            <AnimatePresence>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                sx={{
                  mt: 1,
                  pl: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                }}
              >
                {overnight.map((item) => (
                  <Box
                    key={item}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      color: 'text.secondary',
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        flexShrink: 0,
                        mt: '0.5em',
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: 'text.disabled',
                      }}
                    />
                    <Typography variant="caption" sx={{ lineHeight: 1.55 }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </AnimatePresence>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

AstorBriefingCard.displayName = 'AstorBriefingCard';
AstorBriefingCard.Skeleton = AstorBriefingCardSkeleton;

export { AstorBriefingCardSkeleton };
export default AstorBriefingCard;
